import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  FormControlLabel,
  FormGroup,
  Switch
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { apiAuth, contestServices, LeagueManagementService, teamManagementApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { Helmet } from "react-helmet-async";
import ViewTeamManagement from "./View";
import TableFilter from "../../components/shared/TableFilter";
import { TrimText } from "../../utils/helpers";
import { 
  initializeFromUrlParams, 
  updateSearchParams, 
  updatePageParams, 
  updateFilterParams, 
  updateViewModeParams 
} from "../../utils/urlParams";

export default function TeamManagementList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [country, setCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [league, setLeague] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalTeams, setTotalTeams] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      // country: selectedCountry,
      leagueId: selectedLeague,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await teamManagementApi.getPlayer(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
          
          // Calculate stats
          if (res?.data?.data?.result) {
            const teams = res.data.data.result;
            setTotalTeams(teams.length);
          }
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearchFunction = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
      // Update URL parameter after debounce
      updateSearchParams(searchParams, setSearchParams, query, 1);
    }, 400),
    [searchParams, setSearchParams]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    
    // Don't update URL immediately - let debouncedSearchFunction handle it
    debouncedSearchFunction(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    updatePageParams(searchParams, setSearchParams, page);
  };

  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    setCurrentPage(1);
    
    // Update URL parameter
    updateFilterParams(searchParams, setSearchParams, 'country', newCountry, 1);
  };

  const handleLeagueChange = (newLeague) => {
    setSelectedLeague(newLeague);
    setCurrentPage(1);
    
    // Update URL parameter
    updateFilterParams(searchParams, setSearchParams, 'league', newLeague, 1);
  };

  // Initialize state from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    const searchFromUrl = searchParams.get('search');
    const countryFromUrl = searchParams.get('country');
    const leagueFromUrl = searchParams.get('league');
    const statusFromUrl = searchParams.get('status');
    const pageSizeFromUrl = searchParams.get('pageSize');
    
    // Update state
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
    
    if (searchFromUrl) {
      setSearchItem(searchFromUrl);
      setDebouncedSearch(searchFromUrl); // Set debounced search immediately
    }
    
    if (countryFromUrl) {
      const countryArray = countryFromUrl.split(',').filter(Boolean);
      setSelectedCountry(countryArray);
    }
    
    if (leagueFromUrl) {
      const leagueArray = leagueFromUrl.split(',').filter(Boolean);
      setSelectedLeague(leagueArray);
    }
    
    if (statusFromUrl) {
      const statusArray = statusFromUrl.split(',').map(s => parseInt(s)).filter(s => !isNaN(s));
      setStatus(statusArray);
    }
    
    if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
      setPageSize(parseInt(pageSizeFromUrl));
    }
    
    // Use setTimeout to ensure all state updates are processed
    setTimeout(() => {
      setIsInitialized(true);
    }, 50);
  }, [searchParams]);

  useEffect(() => {
    // Don't trigger debounced search during initialization
    if (!isInitialized) return;
    
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem); 
    }, 500); 

    return () => clearTimeout(delayDebounceFn); 
  }, [searchItem, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
  
    const body = {
      searchItem: debouncedSearch, 
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      leagueId: selectedLeague,
    };
    
    setIsLoading(true)
    setLoaderDelay(false); 
    teamManagementApi.getPlayer(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.result);
            setTotalPages(res?.data?.data?.totalRecords);
            
            // Calculate stats
            if (res?.data?.data?.result) {
              const teams = res.data.data.result;
              setTotalTeams(teams.length);
            }
            
            setLoaderDelay(true); 
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
        setLoaderDelay(true); 
      }) 
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [isInitialized, debouncedSearch, currentPage, pageSize, refresh, status, selectedLeague]);

  const fetchCountry = async () => {
    try {
      const response = await LeagueManagementService.getCountry();

      if (response.status == 200) {
        setCountry(response?.data?.data || [])
      }

    } catch (error) {
      toast.error(error?.response?.data?.message || "Error in fetching Country.")
    }
  }
  useEffect(() => {
    fetchCountry();
  }, [])

  const fetchLeagues = useCallback(async () => {
    try {
      const payload = {
        countryId: selectedCountry,
      }
      const response = await contestServices?.getAllLeagues(payload);
      setLeague(response?.data?.data?.leagues || []);

    } catch (error) {
      errorHandler(error);
    }

  }, [selectedCountry]);
  useEffect(() => {
    fetchLeagues();
  }, [selectedCountry]);

  const renderTeamCard = (team, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={team.teamId}>
      <Paper
        elevation={0}
        onClick={() => navigate(`/team-management/player-view/${team.teamId}/${team.seasonId}`, {
          state: {
            leagueData: team?.leagueData,
            seasonId: team.seasonId,
          }
        })}
        className="team-card"
      >
        {/* Index Number */}
        <Box className="team-card-index-badge">
          <Typography variant="caption" className="team-card-index-text">
            {(currentPage - 1) * pageSize + index + 1}
          </Typography>
        </Box>

        {/* Team Image */}
        <Box className="team-card-image-container">
          <Avatar
            src={team.imagePath}
            alt={team.name}
            className="team-card-avatar"
          />
        </Box>

        {/* Content */}
        <Box className="team-card-content">
          <Typography variant="h6" className="team-card-name">
            {TrimText(team.name, 20)}
          </Typography>
          <Typography variant="body2" className="team-card-league">
            {team?.leagueData?.name || "No League"}
          </Typography>
          <Typography variant="caption" className="team-card-country">
            {team?.leagueData?.country || "No Country"}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1} className="team-card-actions">
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click when button is clicked
              navigate(`/team-management/player-view/${team.teamId}/${team.seasonId}`, {
                state: {
                  leagueData: team?.leagueData,
                  seasonId: team.seasonId,
                }
              });
            }}
            className="team-card-view-button"
          >
            View
          </Button>
        </Stack>
      </Paper>
    </Grid>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Team Name",
      sortable: true,
      selector: (row) => TrimText(row.name, 25),
    },
    {
      name: "League Name",
      sortable: true,
      selector: (row) => row?.leagueData?.name || "-",
    },
    {
      name: "Country",
      sortable: true,
      selector: (row) => row?.leagueData?.country || "-",
    },
    {
      name: "Image",
      width: "120px",
      cell: (row) => (
        <Avatar
          src={row.imagePath}
          alt={row.name}
          sx={{
            width: 50,
            height: 50,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
          }}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => navigate(`/team-management/player-view/${row.teamId}/${row.seasonId}`, {
                state: {
                  leagueData: row?.leagueData,
                  seasonId: row.seasonId,
                }
              })}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];


  return (
    <Container maxWidth="xl" className="team-management-container">
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper elevation={0} className="team-management-header">
            <Stack direction="row" alignItems="center" justifyContent="space-between" className="team-management-header-content">
              <Stack direction="row" alignItems="center" spacing={3} className="team-management-header-left">
                <Avatar className="team-management-header-avatar">
                  <SportsSoccerIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" className="team-management-header-title">
                    Team Management
                  </Typography>
                  <Typography variant="body1" className="team-management-header-subtitle">
                    Manage and monitor all teams in your system
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Controls Section */}
          <Paper elevation={0} className="team-management-controls">
            <Stack direction="row" alignItems="center" justifyContent="space-between" className="team-management-controls-content">
              <Stack direction="row" alignItems="center" spacing={2} className="team-management-controls-left">
                <TableFilter
                  data={country.map(c => ({ value: c?.countryId, label: c?.country }))}
                  value={selectedCountry}
                  setValue={handleCountryChange}
                  filterName="Country"
                />
                <TableFilter
                  data={league.map(l => ({ value: l?.leagueId, label: l?.name }))}
                  value={selectedLeague}
                  setValue={handleLeagueChange}
                  filterName="League"
                />
                <Input
                  value={searchItem}
                  placeholder="Search teams..."
                  onChange={handleSearchChange}
                  className="team-management-search-input"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          const cleared = "";
                          setSearchItem(cleared);
                          setCurrentPage(1);
                          
                          // Update URL parameter
                          updateSearchParams(searchParams, setSearchParams, cleared, 1);
                        }}
                        className="team-management-clear-search-button"
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    updateViewModeParams(searchParams, setSearchParams, newMode);
                  }
                }}
              >
                <ToggleButton value="grid" className="team-management-view-toggle">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list" className="team-management-view-toggle">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper elevation={0} className="team-management-content">
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box className="team-management-grid-container">
                {isLoading || !loaderDelay ? (
                  <Box className="team-management-loading-container">
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box className="team-management-empty-container">
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <SportsSoccerIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" className="team-management-empty-title">
                      No teams found
                    </Typography>
                    <Typography variant="body2" className="team-management-empty-subtitle">
                      Try adjusting your search criteria or check back later
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3} className="team-management-grid">
                      {data.map((team, index) => renderTeamCard(team, index))}
                    </Grid>
                    
                    {/* Pagination for Grid View */}
                    <Box className="team-management-pagination">
                      <Typography variant="body2" className="team-management-pagination-text">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} teams
                      </Typography>
                      
                      {/* Pagination Controls */}
                      <Stack direction="row" spacing={1} alignItems="center" className="team-management-pagination-controls">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                          className="team-management-pagination-button"
                        >
                          Previous
                        </Button>
                        
                        <Typography variant="body2" className="team-management-pagination-info">
                          Page {currentPage} of {Math.ceil(totalPages / pageSize)}
                        </Typography>
                        
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={currentPage >= Math.ceil(totalPages / pageSize)}
                          onClick={() => setCurrentPage(currentPage + 1)}
                          className="team-management-pagination-button"
                        >
                          Next
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
      </Box>
            ) : (
              /* List View */
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    }
                  }}
                  columns={columns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={totalPages}
                  paginationCurrentPage={currentPage}
                  paginationDefaultPage={currentPage}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                    setPageSize(currentRowsPerPage);
                    setCurrentPage(currentPage);
                  }}
                  progressPending={isLoading || !loaderDelay}
                  progressComponent={<Loader />}
                  className="team-management-datatable"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {modalShow ? (
        <ViewTeamManagement
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Container>
  );
}
